import { useContext } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../sporttia/all';
import { useHeaderSlot } from '../HeaderContext';
import PageTitle from './PageTitle';
import { useSidebarState } from './sidebar/SidebarStateContext';
import { useMenuSubtitle } from './sidebar/useMenuSubtitle';

/**
 * Cabecera de la página: sin barra de color, sólo el botón de menú y el título
 * con su subtítulo, igual que en sporttia-competitions. El botón de menú
 * aparece cuando el sidebar está plegado o en móvil.
 *
 * El título lo pinta cada página en el hueco (SttPage lo hace con un portal).
 * Las páginas que no usan SttPage caen al título que publicaron con
 * `setHeader()`, con el subtítulo del menú al que pertenece la ruta.
 */
export default function PageHeader(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { setSlot, hasPageTitle } = useHeaderSlot();
    const { open, isMobile, toggle } = useSidebarState();
    const menuSubtitle = useMenuSubtitle();

    // Igual que competitions: el botón sólo aparece cuando el sidebar está
    // plegado o en móvil. Para plegarlo se usa el atajo ⌘B / Ctrl+B.
    const showMenuButton = isMobile || !open;

    const fallbackTitle = cxt?.header?.title;

    return (
        <nav className="mx-auto flex w-full max-w-(--content-max-width) items-center gap-2 px-6 pt-6 pb-4">
            {showMenuButton && (
                <Tooltip title={t('sidebar.openMenu')} arrow>
                    {/* Botón cuadrado con borde, como el
                        `Button variant="outline" size="icon"` de competitions */}
                    <IconButton
                        id="mobile-menu-button"
                        onClick={toggle}
                        aria-label={t('sidebar.openMenu')}
                        className="border-border hover:bg-primary hover:text-primary-foreground shrink-0 border"
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '6px',
                            padding: 0,
                        }}
                    >
                        <Menu className="h-4 w-4" />
                    </IconButton>
                </Tooltip>
            )}

            {/* El título de la página se pinta aquí con un portal. El de
                reserva va dentro del mismo hueco para que la fila tenga un solo
                elemento flexible y el título no se descoloque. */}
            <div ref={setSlot} className="min-w-0 flex-1">
                {!hasPageTitle && fallbackTitle && (
                    <PageTitle
                        title={fallbackTitle}
                        subtitle={menuSubtitle}
                        compact
                    />
                )}
            </div>
        </nav>
    );
}
